/**
 * 煤棚库存 3D 场景（由 doc/原型/煤棚库存3D展示.html 抽取，供 Vue 挂载）
 */
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { CSS2DRenderer, CSS2DObject } from "three/examples/jsm/renderers/CSS2DRenderer.js";

const EW = 80;
const NS = 360;
const GAP = 20;
const SHED_CLEAR_HEIGHT_M = 30;
const TONS_PER_CUBIC_M = 1;
const PILE_MAX_HEIGHT_RATIO = 0.88;
const SPREAD_FLOOR = 0.2;
const SPREAD_GAIN = 0.68;
const SPREAD_POW = 0.48;
const SPREAD_CAP = 0.82;
const PILE_BASE_MAX_ASPECT = 2.05;
const WORLD_SCALE = 1;
const CAMERA_DISTANCE_MUL = 1.38;
const STACK_PAD_H = 0.12;
const ZONE_PLANE_Y = 0.035;
const ZONE_EDGE_Y = 0.055;
const STACK_EDGE_Y = 0.095;
const RULER_Y = 0.026;

function coalMaterial(seed) {
  let h = 0;
  const str = String(seed || "");
  for (let i = 0; i < str.length; i++) h = (h * 37 + str.charCodeAt(i)) >>> 0;
  const t = (h % 100) / 100;
  const c = new THREE.Color().setHSL(0.06 + t * 0.05, 0.07 + t * 0.06, 0.13 + t * 0.09);
  return new THREE.MeshStandardMaterial({
    color: c,
    roughness: 0.91,
    metalness: 0.05,
    flatShading: true,
  });
}

function disposeObject3D(o) {
  o.traverse((ch) => {
    if (ch.geometry) ch.geometry.dispose();
    if (ch.material) {
      if (Array.isArray(ch.material)) ch.material.forEach((m) => m.dispose());
      else ch.material.dispose();
    }
  });
}

function clearGroup(g) {
  while (g.children.length) {
    const o = g.children[0];
    disposeObject3D(o);
    g.remove(o);
  }
}

export function formatTons(n) {
  const x = Math.round(n * 100) / 100;
  return x >= 10000 ? x.toLocaleString("zh-CN", { maximumFractionDigits: 0 }) : String(x);
}

function stackInventory(s) {
  if (s._disabled) return 0;
  const n = Number(s.inventory);
  return Number.isFinite(n) && n >= 0 ? n : 0;
}

export function stackCapacityTons(s) {
  const c = Number(s.capacityTons);
  if (Number.isFinite(c) && c > 0) return c;
  return s.w * s.h * SHED_CLEAR_HEIGHT_M * TONS_PER_CUBIC_M;
}

function zoneCapacityTons(z) {
  const c = Number(z.capacityTons);
  if (Number.isFinite(c) && c > 0) return c;
  return z.w * z.h * SHED_CLEAR_HEIGHT_M * TONS_PER_CUBIC_M;
}

function zoneUsedInventoryTons(z) {
  return (z.stacks || []).reduce((a, s) => a + stackInventory(s), 0);
}

function stackFillRatio(s) {
  const cap = stackCapacityTons(s);
  if (!(cap > 0)) return 0;
  return THREE.MathUtils.clamp(stackInventory(s) / cap, 0, 1);
}

function createCoalPileGroup(s, fill, seed) {
  const g = new THREE.Group();
  g.userData.pileTopY = 0;
  if (!(fill > 0.0015)) return g;

  const matBase = coalMaterial(seed);
  const segs = 18;
  const inset = Math.max(0.14, Math.min(s.w, s.h) * 0.022);
  const maxRx = Math.max(s.w / 2 - inset, 0.2);
  const maxRz = Math.max(s.h / 2 - inset, 0.2);

  const spread = THREE.MathUtils.clamp(
    SPREAD_FLOOR + SPREAD_GAIN * Math.pow(fill, SPREAD_POW),
    0.16,
    SPREAD_CAP
  );
  let baseRx = maxRx * 0.97 * spread;
  let baseRz = maxRz * 0.97 * spread;
  const rxz = baseRx / baseRz;
  if (rxz > PILE_BASE_MAX_ASPECT) baseRx = baseRz * PILE_BASE_MAX_ASPECT;
  else if (baseRz / baseRx > PILE_BASE_MAX_ASPECT) baseRz = baseRx * PILE_BASE_MAX_ASPECT;

  const hMax = SHED_CLEAR_HEIGHT_M * PILE_MAX_HEIGHT_RATIO;
  const hMain = Math.max(0.28, fill * hMax);

  const coneGeo = new THREE.ConeGeometry(1, 1, segs, 1, false);
  const main = new THREE.Mesh(coneGeo, matBase);
  main.scale.set(baseRx, hMain, baseRz);
  main.position.y = hMain / 2;
  main.castShadow = true;
  main.receiveShadow = true;
  g.add(main);

  g.userData.pileTopY = hMain;

  const seedNum = typeof seed === "string"
    ? seed.split("").reduce((a, c) => a + c.charCodeAt(0), 0)
    : Number(seed) || 0;
  const t = (seedNum % 997) / 997;
  const capH = Math.min(hMain * 0.2, Math.max(fill * hMax * 0.14, 0.12));
  const capR = Math.min(baseRx, baseRz) * (0.16 + t * 0.07);
  if (fill > 0.04 && capR > 0.1 && capH > 0.07) {
    const cap = new THREE.Mesh(coneGeo, matBase.clone());
    cap.scale.set(capR, capH, capR);
    const yAttach = hMain * 0.8;
    cap.position.set(0, yAttach + capH / 2, 0);
    cap.castShadow = true;
    g.add(cap);
    g.userData.pileTopY = yAttach + capH;
  }

  return g;
}

function addZoneGraphics(group, offsetX, z) {
  const cx = offsetX + z.x + z.w / 2;
  const cz = z.y + z.h / 2;
  const fill = new THREE.Mesh(
    new THREE.PlaneGeometry(z.w, z.h),
    new THREE.MeshStandardMaterial({
      color: 0x6366f1,
      transparent: true,
      opacity: 0.09,
      metalness: 0.03,
      roughness: 0.9,
      depthWrite: false,
    })
  );
  fill.rotation.x = -Math.PI / 2;
  fill.position.set(cx, ZONE_PLANE_Y, cz);
  fill.receiveShadow = true;
  group.add(fill);

  const x0 = offsetX + z.x;
  const x1 = offsetX + z.x + z.w;
  const z0 = z.y;
  const z1 = z.y + z.h;
  const y = ZONE_EDGE_Y;
  const pts = [
    new THREE.Vector3(x0, y, z0),
    new THREE.Vector3(x1, y, z0),
    new THREE.Vector3(x1, y, z1),
    new THREE.Vector3(x0, y, z1),
    new THREE.Vector3(x0, y, z0),
  ];
  group.add(new THREE.Line(new THREE.BufferGeometry().setFromPoints(pts), new THREE.LineBasicMaterial({ color: 0x4f46e5 })));

  const capZ = zoneCapacityTons(z);
  const usedZ = zoneUsedInventoryTons(z);
  const div = document.createElement("div");
  div.className = "stack-3d-label zone-3d-label";
  div.textContent = (z.name || "库区") + "\n" + formatTons(usedZ) + " / " + formatTons(capZ) + " 吨";
  div.style.fontSize = "11px";
  div.style.fontWeight = "600";
  div.style.color = "#4338ca";
  div.style.background = "rgba(238,242,255,0.94)";
  div.style.border = "1px solid rgba(99,102,241,0.4)";
  div.style.whiteSpace = "pre-line";
  div.style.textAlign = "center";
  div.style.padding = "3px 8px";
  div.style.borderRadius = "6px";
  const zl = new CSS2DObject(div);
  zl.center.set(0.5, 1);
  zl.position.set(cx, 0.85, cz);
  group.add(zl);
}

function addStackPadAndOutline(group, offsetX, s) {
  const cx = offsetX + s.x + s.w / 2;
  const cz = s.y + s.h / 2;
  const pad = new THREE.Mesh(
    new THREE.BoxGeometry(s.w, STACK_PAD_H, s.h),
    new THREE.MeshStandardMaterial({
      color: s._disabled ? 0xc0c4cc : 0xd3dce8,
      metalness: 0.05,
      roughness: 0.86,
    })
  );
  pad.position.set(cx, STACK_PAD_H / 2, cz);
  pad.receiveShadow = true;
  pad.castShadow = true;
  group.add(pad);

  const x0 = offsetX + s.x;
  const x1 = offsetX + s.x + s.w;
  const z0 = s.y;
  const z1 = s.y + s.h;
  const y = STACK_EDGE_Y;
  const pts = [
    new THREE.Vector3(x0, y, z0),
    new THREE.Vector3(x1, y, z0),
    new THREE.Vector3(x1, y, z1),
    new THREE.Vector3(x0, y, z1),
    new THREE.Vector3(x0, y, z0),
  ];
  group.add(new THREE.Line(new THREE.BufferGeometry().setFromPoints(pts), new THREE.LineBasicMaterial({ color: 0x64748b })));
}

function addShedRulers(group, offsetX, ew, ns, title) {
  const major = 50;
  const minor = 10;
  const majorLen = 3.2;
  const minorLen = 1.35;
  const y = RULER_Y;
  const pos = [];
  const pushSeg = (x1, z1, x2, z2) => {
    pos.push(x1, y, z1, x2, y, z2);
  };

  for (let i = 0; i <= ew; i += minor) {
    const len = i % major === 0 ? majorLen : minorLen;
    pushSeg(offsetX + i, 0, offsetX + i, len);
    pushSeg(offsetX + i, ns, offsetX + i, ns - len);
  }
  for (let j = 0; j <= ns; j += minor) {
    const len = j % major === 0 ? majorLen : minorLen;
    pushSeg(offsetX, j, offsetX + len, j);
    pushSeg(offsetX + ew, j, offsetX + ew - len, j);
  }

  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
  group.add(new THREE.LineSegments(geo, new THREE.LineBasicMaterial({ color: 0x8b9cb8 })));

  function addMeterLabel(x, yl, z, text) {
    const div = document.createElement("div");
    div.className = "ruler-3d-label";
    div.textContent = text;
    div.style.cssText =
      "background:rgba(255,255,255,0.9);border:1px solid rgba(148,163,184,0.45);border-radius:4px;padding:2px 6px;font-size:10px;font-weight:600;color:#475569;pointer-events:none;white-space:nowrap;";
    const lbl = new CSS2DObject(div);
    lbl.center.set(0.5, 1);
    lbl.position.set(x, yl, z);
    group.add(lbl);
  }

  for (let i = 0; i <= ew; i += major) {
    addMeterLabel(offsetX + i, 0.55, -2.2, i + " m");
  }
  for (let j = 0; j <= ns; j += major) {
    if (j === 0) continue;
    addMeterLabel(offsetX - 2.8, 0.55, j, j + " m");
  }
  addMeterLabel(offsetX + ew / 2, 1.1, -4.5, title + " · 东西向 0–" + ew + " m");
  addMeterLabel(offsetX - 5, 1.1, ns / 2, "南北向 0–" + ns + " m");
}

/**
 * @param {HTMLElement} hostElement
 * @param {{ onPick?: (detail: object) => void }} options
 */
export function createCoalStock3dScene(hostElement, options = {}) {
  const onPick = typeof options.onPick === "function" ? options.onPick : () => {};

  let layoutModel = { west: { title: "西煤棚", zones: [] }, east: { title: "东煤棚", zones: [] } };
  let scene;
  let camera;
  let renderer;
  let labelRenderer;
  let controls;
  let worldRoot;
  let westG;
  let eastG;
  let rafId = 0;
  let loopAlive = false;
  let pickPointerDown = null;
  const raycaster = new THREE.Raycaster();

  let zoomWired = false;
  let pickWired = false;
  let resizeWired = false;
  let resizeObserver = null;

  function dollyCamera(factor) {
    if (!camera || !controls) return;
    const target = controls.target;
    const off = new THREE.Vector3().subVectors(camera.position, target);
    const dist = off.length();
    if (dist < 1e-6) return;
    const next = THREE.MathUtils.clamp(dist * factor, controls.minDistance, controls.maxDistance);
    off.normalize().multiplyScalar(next);
    camera.position.copy(target).add(off);
    controls.update();
  }

  function tryPickStackFromEvent(e) {
    if (!camera || !renderer) return;
    const rect = renderer.domElement.getBoundingClientRect();
    const ndcX = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    const ndcY = -((e.clientY - rect.top) / rect.height) * 2 + 1;
    raycaster.setFromCamera(new THREE.Vector2(ndcX, ndcY), camera);
    const hits = raycaster.intersectObjects([westG, eastG], true);
    for (let i = 0; i < hits.length; i++) {
      let o = hits[i].object;
      while (o) {
        const d = o.userData && o.userData.stackPickDetail;
        if (d) {
          onPick(d);
          return;
        }
        o = o.parent;
      }
    }
  }

  function rebuildScene() {
    if (!westG) return;
    clearGroup(westG);
    clearGroup(eastG);

    function buildShed(group, shedSide, offsetX) {
      const meta = shedSide === "west" ? layoutModel.west : layoutModel.east;
      const shedTitle = (meta && meta.title) || (shedSide === "west" ? "西煤棚" : "东煤棚");
      const zones = (meta && meta.zones) || [];

      const floor = new THREE.Mesh(
        new THREE.PlaneGeometry(EW, NS),
        new THREE.MeshStandardMaterial({ color: 0xe8edf5, metalness: 0.06, roughness: 0.82 })
      );
      floor.rotation.x = -Math.PI / 2;
      floor.position.set(offsetX + EW / 2, 0, NS / 2);
      floor.receiveShadow = true;
      group.add(floor);

      const edges = new THREE.LineSegments(
        new THREE.EdgesGeometry(new THREE.PlaneGeometry(EW, NS)),
        new THREE.LineBasicMaterial({ color: 0xa8b8cc })
      );
      edges.rotation.x = -Math.PI / 2;
      edges.position.set(offsetX + EW / 2, 0.02, NS / 2);
      group.add(edges);

      addShedRulers(group, offsetX, EW, NS, shedTitle);

      zones.forEach((z) => {
        addZoneGraphics(group, offsetX, z);
        (z.stacks || []).forEach((s) => {
          addStackPadAndOutline(group, offsetX, s);
        });
      });

      zones.forEach((z) => {
        (z.stacks || []).forEach((s) => {
          const inv = stackInventory(s);
          const cap = stackCapacityTons(s);
          const fill = stackFillRatio(s);
          const cx = offsetX + s.x + s.w / 2;
          const cz = s.y + s.h / 2;

          const pickDetail = {
            stack: s,
            shedSide,
            shedName: shedTitle,
            zoneName: z.name,
          };

          const pile = createCoalPileGroup(s, fill, s.id || s.name || inv);
          pile.position.set(cx, STACK_PAD_H, cz);
          if (pile.children.length) {
            pile.traverse((ch) => {
              if (ch.isMesh) ch.userData.stackPickDetail = pickDetail;
            });
            group.add(pile);
          }

          const pickH = STACK_PAD_H + SHED_CLEAR_HEIGHT_M * PILE_MAX_HEIGHT_RATIO + 2;
          const pickMesh = new THREE.Mesh(
            new THREE.BoxGeometry(s.w * 0.9, pickH, s.h * 0.9),
            new THREE.MeshBasicMaterial({
              transparent: true,
              opacity: 0,
              depthWrite: false,
            })
          );
          pickMesh.position.set(cx, pickH / 2, cz);
          pickMesh.userData.stackPickDetail = pickDetail;
          group.add(pickMesh);

          const peak = pile.userData.pileTopY != null ? pile.userData.pileTopY : 0;
          const topY = STACK_PAD_H + (peak > 0.05 ? peak : 0.45) + 0.75;
          const div = document.createElement("div");
          div.className = "stack-3d-label";
          div.textContent = formatTons(inv) + " / " + formatTons(cap) + " 吨";
          div.style.cssText =
            "background:rgba(255,255,255,0.94);border:1px solid rgba(199,210,254,0.85);border-radius:6px;padding:3px 8px;font-size:10px;font-weight:700;color:#3730a3;pointer-events:none;";
          const lbl = new CSS2DObject(div);
          lbl.center.set(0.5, 1);
          lbl.position.set(cx, topY, cz);
          group.add(lbl);
        });
      });
    }

    buildShed(westG, "west", 0);
    buildShed(eastG, "east", EW + GAP);

    const midX = (EW + GAP + EW) / 2;
    const midZ = NS / 2;
    const tx = midX * WORLD_SCALE;
    const tz = midZ * WORLD_SCALE;
    controls.target.set(tx, 0, tz);
    const bx = 108 * CAMERA_DISTANCE_MUL;
    const by = 78 * CAMERA_DISTANCE_MUL;
    const bz = 118 * CAMERA_DISTANCE_MUL;
    camera.position.set(tx + bx, by, tz + bz);
    controls.update();
  }

  function tick() {
    if (!loopAlive) return;
    rafId = requestAnimationFrame(tick);
    if (controls && renderer && scene && camera) {
      controls.update();
      renderer.render(scene, camera);
      labelRenderer.render(scene, camera);
    }
  }

  function onResize() {
    if (!renderer || !camera || !labelRenderer) return;
    const rw = Math.max(1, Math.floor(hostElement.clientWidth));
    const rh = Math.max(1, Math.floor(hostElement.clientHeight));
    if (rw < 8 || rh < 8) return;
    camera.aspect = rw / rh;
    camera.updateProjectionMatrix();
    renderer.setSize(rw, rh, false);
    labelRenderer.setSize(rw, rh);
  }

  function init() {
    if (renderer) return;

    scene = new THREE.Scene();
    scene.background = new THREE.Color(0xd8e2ee);

    const rw0 = Math.floor(hostElement.clientWidth);
    const rh0 = Math.floor(hostElement.clientHeight);
    const w = rw0 >= 8 ? rw0 : 800;
    const h = rh0 >= 8 ? rh0 : 500;
    camera = new THREE.PerspectiveCamera(46, w / h, 0.5, 2000);
    renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
    renderer.setSize(w, h, false);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    try {
      if (THREE.SRGBColorSpace !== undefined) {
        renderer.outputColorSpace = THREE.SRGBColorSpace;
      }
    } catch (e) {
      /* ignore */
    }
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.08;
    const canvas = renderer.domElement;
    canvas.style.display = "block";
    canvas.style.position = "absolute";
    canvas.style.left = "0";
    canvas.style.top = "0";
    canvas.style.width = "100%";
    canvas.style.height = "100%";
    canvas.style.zIndex = "1";
    hostElement.appendChild(canvas);

    labelRenderer = new CSS2DRenderer();
    labelRenderer.setSize(w, h);
    labelRenderer.domElement.style.position = "absolute";
    labelRenderer.domElement.style.left = "0";
    labelRenderer.domElement.style.top = "0";
    labelRenderer.domElement.style.right = "0";
    labelRenderer.domElement.style.bottom = "0";
    labelRenderer.domElement.style.width = "100%";
    labelRenderer.domElement.style.height = "100%";
    labelRenderer.domElement.style.pointerEvents = "none";
    labelRenderer.domElement.style.zIndex = "2";
    hostElement.appendChild(labelRenderer.domElement);

    controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.06;
    controls.minDistance = 95;
    controls.maxDistance = 520;

    scene.add(new THREE.AmbientLight(0xeef2ff, 0.5));
    const sun = new THREE.DirectionalLight(0xfff5eb, 0.88);
    sun.position.set(72, 118, 52);
    sun.castShadow = true;
    sun.shadow.mapSize.set(2048, 2048);
    sun.shadow.bias = -0.00025;
    scene.add(sun);
    const fillL = new THREE.DirectionalLight(0xc7d2fe, 0.22);
    fillL.position.set(-40, 55, -80);
    scene.add(fillL);

    worldRoot = new THREE.Group();
    worldRoot.scale.setScalar(WORLD_SCALE);
    scene.add(worldRoot);
    westG = new THREE.Group();
    eastG = new THREE.Group();
    worldRoot.add(westG);
    worldRoot.add(eastG);

    if (!pickWired) {
      pickWired = true;
      canvas.addEventListener("pointerdown", (e) => {
        if (e.button !== 0) return;
        pickPointerDown = { x: e.clientX, y: e.clientY, t: Date.now() };
      });
      canvas.addEventListener("pointerup", (e) => {
        if (e.button !== 0 || !pickPointerDown) return;
        const { x, y, t } = pickPointerDown;
        pickPointerDown = null;
        const dx = e.clientX - x;
        const dy = e.clientY - y;
        if (dx * dx + dy * dy > 64) return;
        if (Date.now() - t > 700) return;
        tryPickStackFromEvent(e);
      });
      canvas.addEventListener("pointerleave", () => {
        pickPointerDown = null;
      });
      canvas.addEventListener("pointercancel", () => {
        pickPointerDown = null;
      });
    }

    if (!resizeWired) {
      resizeWired = true;
      window.addEventListener("resize", onResize);
    }

    if (!resizeObserver && typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(() => {
        onResize();
      });
      resizeObserver.observe(hostElement);
    }

    loopAlive = true;
    tick();
    rebuildScene();
  }

  function setLayoutModel(model) {
    layoutModel = model || layoutModel;
    if (!renderer) init();
    else rebuildScene();
  }

  function dispose() {
    loopAlive = false;
    if (rafId) cancelAnimationFrame(rafId);
    rafId = 0;
    if (resizeObserver) {
      try {
        resizeObserver.disconnect();
      } catch (e) {
        /* ignore */
      }
      resizeObserver = null;
    }
    window.removeEventListener("resize", onResize);
    resizeWired = false;
    pickWired = false;
    zoomWired = false;
    if (westG) clearGroup(westG);
    if (eastG) clearGroup(eastG);
    if (renderer) {
      renderer.dispose();
      if (renderer.domElement && renderer.domElement.parentNode === hostElement) {
        hostElement.removeChild(renderer.domElement);
      }
    }
    if (labelRenderer && labelRenderer.domElement && labelRenderer.domElement.parentNode === hostElement) {
      hostElement.removeChild(labelRenderer.domElement);
    }
    renderer = null;
    labelRenderer = null;
    scene = null;
    camera = null;
    controls = null;
    westG = null;
    eastG = null;
    worldRoot = null;
  }

  function bindZoomButtons(btnIn, btnOut) {
    if (zoomWired || !btnIn || !btnOut) return;
    zoomWired = true;
    btnIn.addEventListener("click", () => dollyCamera(0.88));
    btnOut.addEventListener("click", () => dollyCamera(1.12));
  }

  function collectStackCount() {
    let n = 0;
    ["west", "east"].forEach((k) => {
      const part = layoutModel[k];
      (part.zones || []).forEach((z) => {
        n += (z.stacks || []).length;
      });
    });
    return n;
  }

  return {
    init,
    setLayoutModel,
    dispose,
    bindZoomButtons,
    zoomIn: () => dollyCamera(0.88),
    zoomOut: () => dollyCamera(1.12),
    resize: onResize,
    collectStackCount,
    getConstants: () => ({ ew: EW, ns: NS, clearHeight: SHED_CLEAR_HEIGHT_M }),
  };
}
